import { format as formatDate, isValid, parseISO, parse as parseWithPattern } from "date-fns";
import type { DateFormat } from "./date-format.component";

export interface DateFormatSuggestion {
  format: DateFormat;
  matched: number;
  total: number;
  // First value this format parsed, shown so the user can sanity-check the interpretation
  sampleRaw: string;
  sampleParsed: Date;
}

export function formatSampleDate(date: Date): string {
  const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0;
  return formatDate(date, hasTime ? "MMM d, yyyy HH:mm:ss" : "MMM d, yyyy");
}

const PLAUSIBLE_YEAR_MIN = 1900;
const MAX_SAMPLES = 50;
const MAX_SUGGESTIONS = 6;
// A pattern that matches none of the first values is dropped without checking the rest.
const EARLY_EXIT_SAMPLES = 3;

/**
 * Builds every date/time pattern from the usual building blocks, e.g. "dd-MMM-yy" or "M/d/yyyy h:mm a".
 * Padded tokens come first so they win ties against their unpadded twins.
 */
export function generateDatePatterns(): string[] {
  const dayAndMonthStyles = [
    { day: "dd", month: "MM" },
    { day: "d", month: "M" },
    { day: "dd", month: "MMM" },
    { day: "dd", month: "MMMM" },
    { day: "d", month: "MMM" },
    { day: "d", month: "MMMM" },
  ];
  const years = ["yyyy", "yy"];
  const separators = ["/", "-", ".", " "];
  const timeSuffixes = ["", " HH:mm", " HH:mm:ss", " HH:mm:ss.SSS", " h:mm a", " h:mm:ss a"];

  const patterns: string[] = [];
  for (const timeSuffix of timeSuffixes) {
    for (const year of years) {
      for (const { day, month } of dayAndMonthStyles) {
        for (const separator of separators) {
          patterns.push(
            [day, month, year].join(separator) + timeSuffix,
            [month, day, year].join(separator) + timeSuffix,
            [year, month, day].join(separator) + timeSuffix,
          );
        }
      }
    }
  }
  return patterns;
}

// Common formats that Elasticsearch's built-ins do not cover; the Joda pattern is used as the value.
export const COMMON_PATTERNS = [
  "MM/dd/yyyy",
  "dd/MM/yyyy",
  "MM-dd-yyyy",
  "dd-MM-yyyy",
  "yyyy/MM/dd",
  "dd.MM.yyyy",
  "dd-MMM-yyyy",
  "MMM d, yyyy",
  "MM/dd/yyyy HH:mm",
  "MM/dd/yyyy HH:mm:ss",
  "dd/MM/yyyy HH:mm:ss",
  "yyyy-MM-dd HH:mm",
  "yyyy-MM-dd HH:mm:ss",
  "yyyy-MM-dd HH:mm:ss.SSS",
  "MM/dd/yy",
  "dd/MM/yy",
];

/**
 * Ranks the formats that parse the sample values, best match first.
 * e.g. ["08/19/2021", "09/01/2021"] -> [MM/dd/yyyy (2/2), ...]
 */
export function suggestDateFormats(samples: unknown[], formats: DateFormat[]): DateFormatSuggestion[] {
  const values = samples
    .map((sample) => (sample == null ? "" : String(sample).trim()))
    .filter((value) => value !== "")
    .slice(0, MAX_SAMPLES);
  if (values.length === 0) {
    return [];
  }

  const suggestions: DateFormatSuggestion[] = [];
  const seenPatterns = new Set<string>();
  for (const format of rankCandidates(formats)) {
    const key = format.pattern ?? format.value;
    if (seenPatterns.has(key)) {
      continue;
    }
    const parser = parserFor(format);
    if (!parser) {
      continue;
    }
    const parsedValues: { value: string; date: Date }[] = [];
    for (const [index, value] of values.entries()) {
      const date = parser(value);
      if (isPlausible(date)) {
        parsedValues.push({ value, date });
      } else if (index + 1 >= EARLY_EXIT_SAMPLES && parsedValues.length === 0) {
        break;
      }
    }
    if (parsedValues.length === 0) {
      continue;
    }
    seenPatterns.add(key);
    suggestions.push({
      format,
      matched: parsedValues.length,
      total: values.length,
      sampleRaw: parsedValues[0].value,
      sampleParsed: parsedValues[0].date,
    });
  }

  // Array.sort is stable, so candidate order breaks ties between equally good matches
  return dropPaddedTwins(suggestions)
    .sort((a, b) => b.matched - a.matched)
    .slice(0, MAX_SUGGESTIONS);
}

// "dd-MMM-yy" fits a page of "31-Dec-17", but Elasticsearch rejects "9-Dec-17" on a later page; "d" accepts both.
function dropPaddedTwins(suggestions: DateFormatSuggestion[]): DateFormatSuggestion[] {
  const matchedByPattern = new Map(suggestions.map((s) => [s.format.pattern ?? s.format.value, s.matched]));
  return suggestions.filter(({ format, matched }) => {
    const isBuiltIn = format.value !== format.pattern;
    if (isBuiltIn || !format.pattern) {
      return true;
    }
    const twin = unpadded(format.pattern);
    if (twin === format.pattern) {
      return true;
    }
    const twinMatched = matchedByPattern.get(twin);
    return twinMatched === undefined || twinMatched < matched;
  });
}

function unpadded(pattern: string): string {
  return pattern.replace(/(?<!d)dd(?!d)/g, "d").replace(/(?<!M)MM(?!M)/g, "M");
}

// Explicit patterns first, then ISO parsers, then epochs (which match any large number).
function rankCandidates(formats: DateFormat[]): DateFormat[] {
  const isStrict = (format: DateFormat) => Number(format.value.startsWith("strict_"));
  const group = (format: DateFormat) => (format.value.startsWith("epoch_") ? 2 : format.pattern ? 0 : 1);
  return [...formats].sort((a, b) => group(a) - group(b) || isStrict(b) - isStrict(a));
}

function parserFor(format: DateFormat): ((value: string) => Date) | null {
  // Digit counts keep years and small integers (e.g. "2021") from reading as 1970 timestamps
  if (format.value === "epoch_millis") {
    return (value) => (/^\d{13}$/.test(value) ? new Date(Number(value)) : new Date(Number.NaN));
  }
  if (format.value === "epoch_second") {
    return (value) => (/^\d{10}$/.test(value) ? new Date(Number(value) * 1000) : new Date(Number.NaN));
  }
  if (!format.pattern) {
    return (value) => parseISO(value);
  }
  const pattern = jodaToDateFns(format.pattern);
  if (!pattern) {
    return null;
  }
  const referenceDate = new Date(2000, 0, 1);
  // Timezone tokens can't round-trip because date-fns formats in local time
  const mustRoundTrip = !format.pattern.includes("Z");
  return (value) => {
    const date = parseWithPattern(value, pattern, referenceDate);
    // date-fns parses leniently ("dd" accepts "8", "yyyy" accepts "17"); Elasticsearch does not
    if (mustRoundTrip && isValid(date) && formatDate(date, pattern) !== value) {
      return new Date(Number.NaN);
    }
    return date;
  };
}

function isPlausible(date: Date): boolean {
  if (!isValid(date)) {
    return false;
  }
  const year = date.getFullYear();
  return year >= PLAUSIBLE_YEAR_MIN && year <= new Date().getFullYear() + 5;
}

/** Returns null for tokens date-fns can't parse (ordinal day, week-year, week, day-of-week, year-of-era). */
function jodaToDateFns(pattern: string): string | null {
  let result = "";
  let inQuote = false;
  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];
    if (char === "'") {
      inQuote = !inQuote;
      result += char;
      continue;
    }
    if (inQuote) {
      result += char;
      continue;
    }
    if ("DxwWeY".includes(char)) {
      return null;
    }
    if (char === "Z") {
      const isDouble = pattern[i + 1] === "Z";
      result += isDouble ? "xxx" : "xx";
      i += isDouble ? 1 : 0;
      continue;
    }
    result += char;
  }
  return result;
}
